import React, { useEffect, useState } from 'react';
import { Box, Typography, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, Card, CardContent, Chip, useTheme, useMediaQuery, Stack } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { transformarCotizacionAPedido } from '../../utils/cotizacionToPedido';
import cotizacionVentaService from '../../services/cotizacionVentaService';
import pedidoVentaService, { PedidoVenta, PedidoVentaDetalle } from '../../services/pedidoVentaService';
import clienteService from '../../services/clienteService';
import productoService from '../../services/productoService';
import movMonedaService from '../../services/movMonedaService';
import ubicacionService from '../../services/ubicacionService';
import transporteService from '../../services/transporteService';
import { IUbicacion } from '../../interfaces/Ubicacion';
import PedidoForm from './components/PedidoForm';
import ClienteSearch from '../../components/Tesoreria/ClienteSearch';
import ProductoSearch from './components/ProductoSearch';
import PDFViewer from '../../components/shared/PDFViewer';

type PedidoVentaUI = PedidoVenta & { ordenCompra?: string; transporte?: string; ubicacion?: string; formaDePago?: string };
type PedidoVentaUIExt = PedidoVentaUI & { nroSuc?: number; cuit?: string; tipoIVA?: string; idCompania?: string };

const FORMAS_DE_PAGO_VALIDAS = ['CONTADO', 'CTA.CTE.', 'EFECTIVO', 'DEVOLUCION', 'CONTRAREEMBOLSO'];
const DEFAULT_NRO_SUC = 1;

const obtenerTransporteSugerido = (cliente?: { nroExpreso?: number | string | null } | null): string => {
  if (!cliente) {
    return '';
  }
  const rawValue = cliente.nroExpreso ?? (cliente as any)?.nroexpreso ?? null;
  if (rawValue === undefined || rawValue === null) {
    return '';
  }
  const strValue = String(rawValue).trim();
  if (!strValue || strValue === '0') {
    return '';
  }
  return strValue;
};

const normalizarFormaDePago = (valor?: string | null): string => {
  if (!valor) {
    return '';
  }
  const cleaned = String(valor).trim().toUpperCase();
  if (!cleaned) {
    return '';
  }
  const match = FORMAS_DE_PAGO_VALIDAS.find(forma => forma.toUpperCase() === cleaned);
  return match || '';
};

const obtenerFormaPagoSugerida = (cliente?: { condVta?: string | null } | null): string => {
  if (!cliente) {
    return 'EFECTIVO';
  }
  const rawValue = cliente.condVta ?? (cliente as any)?.condvta ?? null;
  return normalizarFormaDePago(rawValue);
};

const normalizarPorcentajeBonificacion = (valor?: number | string | null): number => {
  if (valor === null || valor === undefined) {
    return 0;
  }
  const numero = Number(valor);
  if (!Number.isFinite(numero)) {
    return 0;
  }
  return Math.max(0, Math.min(100, numero));
};

const PedidosVenta: React.FC = () => {
  const navigate = useNavigate();
  const { pedidoId } = useParams<{ pedidoId?: string }>();
  const [searchParams] = useSearchParams();
  const cotizacionIdParam = searchParams.get('cotizacionId');
  const cotizacionId = cotizacionIdParam ? parseInt(cotizacionIdParam, 10) : null;
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [loadingPedido, setLoadingPedido] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [infoCotizacion, setInfoCotizacion] = useState<{ letra?: string; puntoVenta?: number; nroComprobante?: number; nroFormulario?: number } | null>(null);
  
  // Determinar el modo: 'crear', 'editar', o 'ver'
  const modo = pedidoId 
    ? (location.pathname.includes('/ver') ? 'ver' : 'editar')
    : 'crear';
  const isReadOnly = modo === 'ver';
  const isEditMode = modo === 'editar';
  const [pedidoVenta, setPedidoVenta] = useState<PedidoVentaUIExt>({
    nroFormulario: 0,
    nroTipoFormulario: '12',
    tipoCliente: 'C',
    fechaEmision: new Date(),
    horaEmision: new Date(),
    nroCliente: 0,
    nombre: '',
    nroVendedor: 0,
    nroLista: '',
    codigoMoneda: 'ARS',
    importeTotal: 0,
    asentado: 'N',
    descripcion: '',
    usuario: '',
    ordenCompra: '',
    transporte: '',
    ubicacion: '',
    formaDePago: '',
    nroSuc: DEFAULT_NRO_SUC,
    cuit: '',
    tipoIVA: '',
    idCompania: '',
    detalles: [],
    porcDesc: 0,
    porcDesc2: 0,
    porcDesc3: 0,
  });
  const [clienteDialogOpen, setClienteDialogOpen] = useState(false);
  const [productoDialogOpen, setProductoDialogOpen] = useState(false);
  const [productos, setProductos] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [nroPedidoCreado, setNroPedidoCreado] = useState<number | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loadingPDF, setLoadingPDF] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [cotizacionUSD, setCotizacionUSD] = useState<number | null>(null);
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const round5 = (n: number) => Math.round(n * 100000) / 100000; // Redondeo a 5 decimales para montos unitarios

  useEffect(() => {
    (async () => {
      try {
        const v = await movMonedaService.getUltimaCotizacion();
        if (v && Number.isFinite(v) && v > 0) {
          setCotizacionUSD(v);
        } else {
          setCotizacionUSD(null);
        }
      } catch {
        setCotizacionUSD(null);
      }
    })();
  }, []);

  // Resetear infoCotizacion cuando no hay cotizacionId
  useEffect(() => {
    if (!cotizacionId || isNaN(cotizacionId)) {
      setInfoCotizacion(null);
    }
  }, [cotizacionId]);

  // Cargar cotización para pre-cargar pedido (modo crear con ?cotizacionId=)
  useEffect(() => {
    if (cotizacionId && !isNaN(cotizacionId) && !pedidoId) {
      const cargarDesdeCotizacion = async () => {
        try {
          setLoadingPedido(true);
          setError(null);
          const cotizacion = await cotizacionVentaService.getCotizacionVenta(cotizacionId);
          const cotizacionExt = cotizacion as any;
          setInfoCotizacion({
            letra: cotizacionExt.letra || '',
            puntoVenta: cotizacionExt.nroSuc ?? 0,
            nroComprobante: cotizacionExt.nroComprobante ?? 0,
            nroFormulario: cotizacion.nroFormulario
          });
          let ubicacionFormateada = cotizacionExt.ubicacion || '';
          const nroUbicacionOrigen = cotizacionExt.nroUbicacionOrigen || cotizacionExt.nroUbicacion;
          if (!ubicacionFormateada && nroUbicacionOrigen) {
            try {
              const ubicaciones = await ubicacionService.getUbicaciones();
              const ubicacionEncontrada = ubicaciones.find(u => u.nroUbicacion === nroUbicacionOrigen);
              ubicacionFormateada = ubicacionEncontrada ? `${ubicacionEncontrada.nombreUbicacion} (${ubicacionEncontrada.nroUbicacion})` : `(${nroUbicacionOrigen})`;
            } catch {
              ubicacionFormateada = nroUbicacionOrigen?.toString() || '';
            }
          }
          let transporteValue = (cotizacion as any).transporte || '';
          const nroExpreso = cotizacionExt.nroExpreso;
          if (!transporteValue && nroExpreso !== undefined && nroExpreso !== null && nroExpreso !== 0 && nroExpreso !== '') {
            transporteValue = String(nroExpreso);
          } else if (transporteValue) {
            const match = String(transporteValue).match(/\((\d+)\)/);
            transporteValue = match && match[1] ? match[1] : String(transporteValue);
          }
          const pedidoTransformado = transformarCotizacionAPedido(cotizacion);
          const detallesConSubtotales = pedidoTransformado.detalles?.map((d) => ({
            ...d,
            precioUnitario: (d as any).precioLista || d.precioUnitario,
            descuento: (d as any).porcDesc ?? d.descuento,
          })) || [];
          const importeTotalCalculado = detallesConSubtotales.reduce((sum, d) => sum + (d.subtotal || 0), 0);
          const fechaEmisionDate = pedidoTransformado.fechaEmision ? (pedidoTransformado.fechaEmision instanceof Date ? pedidoTransformado.fechaEmision : new Date(pedidoTransformado.fechaEmision)) : new Date();
          const horaEmisionDate = pedidoTransformado.horaEmision ? (pedidoTransformado.horaEmision instanceof Date ? pedidoTransformado.horaEmision : new Date(pedidoTransformado.horaEmision)) : new Date();
          setPedidoVenta({
            ...pedidoTransformado,
            fechaEmision: fechaEmisionDate,
            horaEmision: horaEmisionDate,
            ordenCompra: cotizacionExt.ordenDeCompra || cotizacionExt.ordenCompra || '',
            transporte: transporteValue,
            ubicacion: ubicacionFormateada,
            formaDePago: cotizacionExt.formaDePago || '',
            nroSuc: cotizacionExt.nroSuc || DEFAULT_NRO_SUC,
            cuit: cotizacionExt.cuit || '',
            tipoIVA: cotizacionExt.tipoIVA || cotizacionExt.tipoIva || '',
            idCompania: cotizacionExt.idCompania || '',
            detalles: detallesConSubtotales,
            importeTotal: pedidoTransformado.importeTotal && pedidoTransformado.importeTotal > 0 ? pedidoTransformado.importeTotal : round2(importeTotalCalculado),
          } as PedidoVentaUIExt);
        } catch (err: any) {
          const message = err?.response?.data || 'Error al cargar la cotización';
          setError(message);
          enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
        } finally {
          setLoadingPedido(false);
        }
      };
      cargarDesdeCotizacion();
      return;
    }

    // Cargar pedido existente si estamos en modo edición o solo lectura
    if (pedidoId) {
      const cargarPedido = async () => {
        try {
          setLoadingPedido(true);
          setError(null);
          const id = parseInt(pedidoId, 10);
          if (isNaN(id)) {
            setError('ID de pedido inválido');
            navigate('/ventas/pedidos');
            return;
          }

          const pedidoCargado = await pedidoVentaService.getPedidoVenta(id);
          
          // Log para ver todos los campos que llegan del backend
          console.log('🔍 Pedido completo del backend:', pedidoCargado);
          console.log('🔍 Todos los campos del pedido:', Object.keys(pedidoCargado));
          console.log('🔍 Campos relacionados con transporte:', Object.keys(pedidoCargado).filter(k => 
            k.toLowerCase().includes('expreso') || 
            k.toLowerCase().includes('transporte') ||
            k.toLowerCase().includes('transport')
          ));
          
          // Mapear los datos del pedido al formato esperado
          // Incluir campos adicionales que pueden venir del backend o necesitarse
          
          // Obtener ubicación formateada si hay nroUbicacionOrigen
          // El backend no devuelve nroUbicacionOrigen en el GET, así que intentamos obtenerlo
          // de diferentes campos posibles o de una llamada adicional si es necesario
          let ubicacionFormateada = (pedidoCargado as any).ubicacion || '';
          
          // Intentar obtener nroUbicacionOrigen de diferentes campos posibles
          const nroUbicacionOrigen = (pedidoCargado as any).nroUbicacionOrigen 
            || (pedidoCargado as any).nroUbicacion 
            || (pedidoCargado as any).ubicacionOrigen
            || (pedidoCargado as any).codigoUbicacion
            || (pedidoCargado as any).formulario?.nroUbicacionOrigen;
          
          // Si no hay ubicación formateada pero sí hay nroUbicacionOrigen, buscar la ubicación
          if (!ubicacionFormateada && nroUbicacionOrigen) {
            try {
              const ubicaciones = await ubicacionService.getUbicaciones();
              const ubicacionEncontrada = ubicaciones.find(u => u.nroUbicacion === nroUbicacionOrigen);
              
              if (ubicacionEncontrada) {
                ubicacionFormateada = `${ubicacionEncontrada.nombreUbicacion} (${ubicacionEncontrada.nroUbicacion})`;
              } else {
                // Si no se encuentra, usar solo el número como fallback
                ubicacionFormateada = `(${nroUbicacionOrigen})`;
              }
            } catch (error) {
              console.warn('Error al cargar ubicación:', error);
              // Si hay error, usar solo el número como fallback
              if (nroUbicacionOrigen) {
                ubicacionFormateada = nroUbicacionOrigen.toString();
              }
            }
          }
          
          // Si aún no hay ubicación, el campo quedará vacío y el usuario podrá seleccionarla
          // Esto es normal ya que el backend no devuelve este campo en el GET del pedido
          
          // Obtener transporte - el Select espera solo el número como string, no el formato "NOMBRE (número)"
          // El campo nroExpreso viene directamente en el objeto del pedido según la respuesta del backend
          let transporteValue = (pedidoCargado as any).transporte || '';
          const nroExpreso = (pedidoCargado as any).nroExpreso;
          
          // Si no hay transporte pero sí hay nroExpreso, usar el número directamente
          // El Select mostrará el formato "NOMBRE (número)" automáticamente basándose en el value
          if (!transporteValue && nroExpreso !== undefined && nroExpreso !== null && nroExpreso !== 0 && nroExpreso !== '') {
            // El Select espera el número como string para que coincida con el value de los MenuItem
            transporteValue = String(nroExpreso);
          } else if (transporteValue) {
            // Si ya hay un valor, verificar si está en formato "NOMBRE (número)" y extraer solo el número
            const match = String(transporteValue).match(/\((\d+)\)/);
            if (match && match[1]) {
              transporteValue = match[1];
            } else {
              // Si no tiene el formato, usar el valor tal cual (debe ser el número)
              transporteValue = String(transporteValue);
            }
          }
          
          // Usar directamente los valores del GET del backend (precioLista, porcDesc, subtotal)
          const detallesConSubtotales = pedidoCargado.detalles?.map((detalle) => {
            // Usar directamente precioLista, porcDesc y subtotal del backend
            const precioLista = (detalle as any).precioLista || detalle.precioUnitario || 0;
            const porcDesc = (detalle as any).porcDesc || 0;
            const subtotal = detalle.subtotal || 0;
            
            return {
              ...detalle,
              // Guardar precioLista en precioUnitario para el estado (compatibilidad)
              precioUnitario: precioLista,
              // Guardar porcDesc en descuento para el estado (compatibilidad)
              descuento: porcDesc,
              // Usar subtotal directamente del backend
              subtotal: subtotal,
            };
          }) || [];
          
          // Calcular importeTotal sumando los subtotales de los detalles
          const importeTotalCalculado = detallesConSubtotales.reduce((sum, d) => {
            return sum + (d.subtotal || 0);
          }, 0);
          
          // Convertir fechas de string a Date si vienen como strings desde el backend
          const fechaEmisionDate = pedidoCargado.fechaEmision 
            ? (pedidoCargado.fechaEmision instanceof Date 
                ? pedidoCargado.fechaEmision 
                : new Date(pedidoCargado.fechaEmision))
            : new Date();
          
          const horaEmisionDate = pedidoCargado.horaEmision
            ? (pedidoCargado.horaEmision instanceof Date
                ? pedidoCargado.horaEmision
                : new Date(pedidoCargado.horaEmision))
            : new Date();

          setPedidoVenta({
            ...pedidoCargado,
            fechaEmision: fechaEmisionDate,
            horaEmision: horaEmisionDate,
            ordenCompra: (pedidoCargado as any).ordenCompra || (pedidoCargado as any).ordenDeCompra || '',
            transporte: transporteValue,
            ubicacion: ubicacionFormateada,
            formaDePago: (pedidoCargado as any).formaDePago || '',
            nroSuc: (pedidoCargado as any).nroSuc || DEFAULT_NRO_SUC,
            cuit: (pedidoCargado as any).cuit || '',
            tipoIVA: (pedidoCargado as any).tipoIVA || (pedidoCargado as any).tipoIva || '',
            dni: (pedidoCargado as any).dni || '',
            idCompania: (pedidoCargado as any).idCompania || '',
            // Usar detalles con subtotales calculados
            detalles: detallesConSubtotales,
            // Usar importeTotal del backend si está presente y es válido, sino calcularlo desde los detalles
            importeTotal: pedidoCargado.importeTotal && pedidoCargado.importeTotal > 0
              ? pedidoCargado.importeTotal
              : round2(importeTotalCalculado),
          } as PedidoVentaUIExt);
        } catch (err: any) {
          const message = err?.response?.data || 'Error al cargar el pedido';
          setError(message);
          enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
          // Redirigir a la lista después de un tiempo
          setTimeout(() => {
            navigate('/ventas/pedidos');
          }, 2000);
        } finally {
          setLoadingPedido(false);
        }
      };

      cargarPedido();
    }
  }, [pedidoId, cotizacionId, navigate, enqueueSnackbar]);

  const handleProductoSearch = async () => {
    try {
      setLoading(true);
      const response = await productoService.getProductos({
        searchTerm,
        pageNumber: 1,
        pageSize: 50
      });
      setProductos(response.items);
    } catch (err: any) {
      enqueueSnackbar('Error al buscar productos', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClienteSelect = (cliente: any) => {
    const transporteSugerido = obtenerTransporteSugerido(cliente);
    const formaPagoSugerida = obtenerFormaPagoSugerida(cliente);
    setPedidoVenta((prev: PedidoVentaUIExt) => ({
      ...prev,
      nroCliente: cliente.nroCliente ?? cliente.codigo ?? 0,
      nombre: cliente.nombre,
      cuit: cliente.cuit || '',
      dni: (cliente as any).dni || '',
      tipoIVA: cliente.tipoIva || '',
      formaDePago: formaPagoSugerida,
      transporte: transporteSugerido,
      porcDesc: normalizarPorcentajeBonificacion(
        cliente.porcDesc ?? (cliente as any)?.porc_Desc ?? (cliente as any)?.porcdesc
      ),
      porcDesc2: normalizarPorcentajeBonificacion(
        cliente.porcDesc2 ?? (cliente as any)?.porc_Desc2 ?? (cliente as any)?.porcdesc2
      ),
      porcDesc3: normalizarPorcentajeBonificacion(
        cliente.porcDesc3 ?? (cliente as any)?.porc_Desc3 ?? (cliente as any)?.porcdesc3
      ),
    }));
    setClienteDialogOpen(false);
  };

  const handleProductoSelect = (producto: any) => {
    if (!pedidoVenta.nroCliente || pedidoVenta.nroCliente <= 0) {
      enqueueSnackbar('Debe seleccionar un cliente antes de agregar productos al carrito', { variant: 'warning' });
      return;
    }
    const base = Number(producto.precioVenta ?? producto.precio ?? producto.costo ?? producto.precioLista ?? producto.precioUnitario ?? 0);
    const tipoMoneda = producto.tipoMoneda ?? producto.tipoMonedaArticulo ?? 0;
    const baseRed = round2(base);
    // Solo convertir a ARS si hay cotización disponible, de lo contrario mantener precio original (USD)
    const precio = (Number(tipoMoneda) === 1 && cotizacionUSD && cotizacionUSD > 0) ? round2(baseRed * cotizacionUSD) : baseRed;
    const nuevoDetalle: PedidoVentaDetalle = {
      nroIdformulario: pedidoVenta.nroFormulario,
      nroItem: pedidoVenta.detalles.length + 1,
      codigoArticulo: producto.codigoArticulo,
      descripcionArticulo: producto.descripcion,
      cantidad: 1,
      precioUnitario: precio,
      descuento: 0,
      subtotal: precio,
    };

    setPedidoVenta((prev: PedidoVentaUIExt) => ({
      ...prev,
      detalles: [...prev.detalles, nuevoDetalle],
      importeTotal: (prev.importeTotal || 0) + precio,
    }));
    setProductoDialogOpen(false);
  };

  // Persistir sucursal ingresada si es válida
  useEffect(() => {
    const v = Number(pedidoVenta.nroSuc);
    if (Number.isFinite(v) && v > 0) {
      try {
        localStorage.setItem('nroSuc', String(v));
        localStorage.setItem('sucursal', String(v));
      } catch {}
    }
  }, [pedidoVenta.nroSuc]);

  // Búsqueda automática por número de cliente con debounce, igual a Recibo
  // En modo edición, no sobrescribir transporte y formaDePago si ya existen (vienen del pedido guardado)
  useEffect(() => {
    const value = pedidoVenta.nroCliente;
    const handle = setTimeout(() => {
      const nro = parseInt((value ?? 0).toString());
      if (!isNaN(nro) && nro > 0) {
        (async () => {
          try {
            const c = await clienteService.getCliente(nro);
            const transporteSugerido = obtenerTransporteSugerido(c);
            const formaPagoSugerida = obtenerFormaPagoSugerida(c);
            
            setPedidoVenta(prev => {
              // En modo edición, preservar transporte, formaDePago y porcentajes de descuento si ya existen
              const transportePreservar = (isEditMode || isReadOnly) && prev.transporte 
                ? prev.transporte 
                : transporteSugerido;
              
              const formaPagoPreservar = (isEditMode || isReadOnly) && (prev as PedidoVentaUI).formaDePago
                ? (prev as PedidoVentaUI).formaDePago
                : formaPagoSugerida;
              
              // Preservar porcentajes de descuento en modo edición
              const porcDescPreservar = (isEditMode || isReadOnly) && (prev.porcDesc !== undefined && prev.porcDesc !== null)
                ? prev.porcDesc
                : normalizarPorcentajeBonificacion(
                    c.porcDesc ?? (c as any)?.porc_Desc ?? (c as any)?.porcdesc
                  );
              
              const porcDesc2Preservar = (isEditMode || isReadOnly) && (prev.porcDesc2 !== undefined && prev.porcDesc2 !== null)
                ? prev.porcDesc2
                : normalizarPorcentajeBonificacion(
                    c.porcDesc2 ?? (c as any)?.porc_Desc2 ?? (c as any)?.porcdesc2
                  );
              
              const porcDesc3Preservar = (isEditMode || isReadOnly) && (prev.porcDesc3 !== undefined && prev.porcDesc3 !== null)
                ? prev.porcDesc3
                : normalizarPorcentajeBonificacion(
                    c.porcDesc3 ?? (c as any)?.porc_Desc3 ?? (c as any)?.porcdesc3
                  );
              
              const cuitPreservar = (isEditMode || isReadOnly) && prev.cuit
                ? prev.cuit
                : (c.cuit || '');
              const tipoIVAPreservar = (isEditMode || isReadOnly) && prev.tipoIVA
                ? prev.tipoIVA
                : (c.tipoIva || '');
              return {
                ...prev,
                nroCliente: c.nroCliente,
                nombre: c.nombre,
                cuit: cuitPreservar,
                dni: (c as any).dni || '',
                tipoIVA: tipoIVAPreservar,
                formaDePago: formaPagoPreservar,
                transporte: transportePreservar,
                porcDesc: porcDescPreservar,
                porcDesc2: porcDesc2Preservar,
                porcDesc3: porcDesc3Preservar,
              } as PedidoVentaUIExt;
            });
          } catch {}
        })();
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [pedidoVenta.nroCliente, isEditMode, isReadOnly]);

  const handleSave = async () => {
    // No permitir guardar en modo solo lectura
    if (isReadOnly) {
      enqueueSnackbar('No se puede guardar un pedido en modo solo lectura', { variant: 'warning' });
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Validar datos obligatorios antes de enviar
      const erroresValidacion: string[] = [];

      // Validar cliente
      if (!pedidoVenta.nroCliente || pedidoVenta.nroCliente <= 0) {
        erroresValidacion.push('Debe seleccionar un cliente');
      }

      // Validar fecha de emisión
      if (!pedidoVenta.fechaEmision) {
        erroresValidacion.push('Debe ingresar la Fecha de Emisión en la sección Información del Pedido');
      }

      // Validar ubicación
      const ubicacion = (pedidoVenta as PedidoVentaUI).ubicacion;
      if (!ubicacion || ubicacion.trim() === '') {
        erroresValidacion.push('Debe seleccionar una Ubicación en la sección Información del Pedido');
      }

      // Validar forma de pago
      const formaDePago = (pedidoVenta as PedidoVentaUI).formaDePago;
      if (!formaDePago || formaDePago.trim() === '') {
        erroresValidacion.push('Debe seleccionar una Forma de Pago en la sección Información del Pedido');
      }

      // Nota: Orden de Compra y Transporte son campos opcionales, no se validan

      // Validar detalles
      if (!pedidoVenta.detalles || pedidoVenta.detalles.length === 0) {
        erroresValidacion.push('Debe agregar al menos un producto al pedido');
      } else {
        // Validar cada detalle
        pedidoVenta.detalles.forEach((detalle, index) => {
          if (!detalle.cantidad || detalle.cantidad <= 0) {
            erroresValidacion.push(`El producto "${detalle.descripcionArticulo || detalle.codigoArticulo}" (item ${index + 1}) debe tener una cantidad mayor a 0`);
          }
          if (!detalle.codigoArticulo) {
            erroresValidacion.push(`El item ${index + 1} no tiene código de artículo`);
          }
        });
      }

      // Mostrar todos los errores de validación
      if (erroresValidacion.length > 0) {
        erroresValidacion.forEach((error) => {
          enqueueSnackbar(error, { variant: 'error', autoHideDuration: 5000 });
        });
        setError(erroresValidacion.join('\n'));
        setLoading(false);
        return;
      }

      // Preparar el payload incluyendo los campos adicionales necesarios para el endpoint
      const payload = {
        ...pedidoVenta,
        nroSuc: pedidoVenta.nroSuc || DEFAULT_NRO_SUC,
        ordenCompra: pedidoVenta.ordenCompra || '',
        transporte: pedidoVenta.transporte || '',
        ubicacion: pedidoVenta.ubicacion || '',
        formaDePago: pedidoVenta.formaDePago || '',
        // Incluir nroFormularioCotizacion si el pedido proviene de Preparar Pedido (marca cotización como facturada)
        nroFormularioCotizacion: (!isEditMode && cotizacionId && !isNaN(cotizacionId)) ? cotizacionId : undefined,
      } as any;
      
      let response: PedidoVenta;
      let nroPedido: number | null = null;

      if (isEditMode && pedidoId) {
        // Modo edición: usar updatePedidoVenta
        const id = parseInt(pedidoId, 10);
        await pedidoVentaService.updatePedidoVenta(id, payload);
        nroPedido = id;
        // Cargar el pedido actualizado
        response = await pedidoVentaService.getPedidoVenta(id);
        enqueueSnackbar('Pedido actualizado exitosamente', { variant: 'success', autoHideDuration: 3000 });
        // Redirigir a la lista después de actualizar
        setTimeout(() => {
          navigate('/ventas/pedidos');
        }, 1500);
        setLoading(false);
        return; // Salir aquí para evitar ejecutar el código de creación
      } else {
        // Modo creación: usar createPedidoVenta
        response = await pedidoVentaService.createPedidoVenta(payload);
      
        // Capturar el número de pedido de la respuesta
        // La respuesta puede venir en diferentes formatos según el backend
        if (response?.nroFormulario) {
          nroPedido = response.nroFormulario;
        } else if ((response as any)?.formulario?.nroFormulario) {
          nroPedido = (response as any).formulario.nroFormulario;
        } else if ((response as any)?.data?.nroFormulario) {
          nroPedido = (response as any).data.nroFormulario;
        }
      }
      
      // Si se obtuvo el número de pedido y estamos en modo creación, cargar el pedido completo desde el servidor
      if (nroPedido && !isEditMode) {
        try {
          // Cargar el pedido recién creado desde el servidor
          const pedidoCargado = await pedidoVentaService.getPedidoVenta(nroPedido);
          
          // Actualizar el estado con el pedido cargado (mantener campos UI adicionales)
          setPedidoVenta({
            ...pedidoCargado,
            ordenCompra: pedidoVenta.ordenCompra || '',
            transporte: pedidoVenta.transporte || '',
            ubicacion: pedidoVenta.ubicacion || '',
            formaDePago: pedidoVenta.formaDePago || '',
            nroSuc: (pedidoCargado as any).nroSuc || pedidoVenta.nroSuc || DEFAULT_NRO_SUC,
            cuit: pedidoVenta.cuit || '',
            tipoIVA: pedidoVenta.tipoIVA || '',
            idCompania: pedidoVenta.idCompania || '',
          } as PedidoVentaUIExt);

          setNroPedidoCreado(nroPedido);
          setSuccessDialogOpen(true);
          
          // Descargar el PDF del pedido desde el backend
          try {
            setLoadingPDF(true);
            
            // Obtener el PDF del backend
            const blob = await pedidoVentaService.getPedidoVentaPDF(nroPedido);
            const url = window.URL.createObjectURL(blob);
            
            // Descargar automáticamente el PDF
            const fileName = `Pedido_${nroPedido}.pdf`;
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // También establecer la URL para que se pueda ver en el visor si el usuario lo desea
            setPdfUrl(url);
            
            // Cerrar el diálogo de éxito y mostrar el PDF automáticamente
            setTimeout(() => {
              setSuccessDialogOpen(false);
            }, 1500);
            
            enqueueSnackbar(`PDF del pedido #${nroPedido} descargado correctamente`, { variant: 'success', autoHideDuration: 3000 });
          } catch (pdfError) {
            console.error('Error al descargar el PDF del pedido:', pdfError);
            enqueueSnackbar('Error al descargar el PDF del pedido', { variant: 'warning' });
          } finally {
            setLoadingPDF(false);
          }
        } catch (loadError: any) {
          console.error('Error al cargar el pedido:', loadError);
          // Aún así mostrar el éxito si se creó el pedido
          enqueueSnackbar('Pedido creado exitosamente, pero no se pudo cargar la información completa', { variant: 'warning' });
          // Limpiar formulario solo si no se pudo cargar el pedido
          setPedidoVenta({
            nroFormulario: 0,
            nroTipoFormulario: '12',
            tipoCliente: 'C',
            fechaEmision: new Date(),
            horaEmision: new Date(),
            nroCliente: 0,
            nombre: '',
            nroVendedor: 0,
            nroLista: '',
            codigoMoneda: 'ARS',
            importeTotal: 0,
            asentado: 'N',
            descripcion: '',
            usuario: '',
            ordenCompra: '',
            transporte: '',
            ubicacion: '',
            formaDePago: '',
            nroSuc: DEFAULT_NRO_SUC,
            cuit: '',
            tipoIVA: '',
            idCompania: '',
            detalles: [],
          });
        }
      } else {
        // Si no se puede obtener el número de pedido, mostrar snackbar genérico y limpiar formulario
        enqueueSnackbar('Pedido de venta guardado exitosamente', { variant: 'success' });
        setPedidoVenta({
          nroFormulario: 0,
          nroTipoFormulario: '12',
          tipoCliente: 'C',
          fechaEmision: new Date(),
          horaEmision: new Date(),
          nroCliente: 0,
          nombre: '',
          nroVendedor: 0,
          nroLista: '',
          codigoMoneda: 'ARS',
          importeTotal: 0,
          asentado: 'N',
          descripcion: '',
          usuario: '',
          ordenCompra: '',
          transporte: '',
          nroSuc: DEFAULT_NRO_SUC,
          cuit: '',
          tipoIVA: '',
          idCompania: '',
          detalles: [],
        });
      }
    } catch (err: any) {
      console.error('Error al guardar pedido:', err);
      
      // Manejo detallado de errores
      const status = err?.response?.status;
      const data = err?.response?.data;
      let errorMessage = 'Error al guardar el pedido de venta';
      let mostrado = false;

      // Si el error contiene mensaje de validación (viene del servicio)
      if (err?.message && (!err?.response || err.message.includes('Se encontraron') || err.message.includes('obligatorio') || err.message.includes('obligatoria'))) {
        errorMessage = err.message;
        
        // Extraer errores individuales del mensaje
        let errores: string[] = [];
        if (err.message.includes('Se encontraron')) {
          // Formato: "Se encontraron X errores:\n1. error1\n2. error2"
          const partes = err.message.split('\n');
          errores = partes.slice(1).map((e: string) => e.replace(/^\d+\.\s*/, '').trim()).filter((e: string) => e);
        } else if (err.message.includes('\n')) {
          // Formato multilínea simple
          errores = err.message.split('\n').map((e: string) => e.trim()).filter((e: string) => e && !e.includes('Errores de validación'));
        } else {
          // Error único
          errores = [err.message];
        }
        
        // Mostrar cada error de validación en un snackbar separado
        errores.forEach((error: string) => {
          if (error.trim()) {
            enqueueSnackbar(error.trim(), { variant: 'error', autoHideDuration: 6000 });
          }
        });
        
        // También mostrar el error completo en el Alert
        if (errores.length > 1) {
          setError(`Se encontraron ${errores.length} errores:\n${errores.map((e, i) => `${i + 1}. ${e}`).join('\n')}`);
        } else if (errores.length === 1) {
          setError(errores[0]);
        } else {
          setError(errorMessage);
        }
        mostrado = true;
      }
      // Manejar errores de validación del servidor (400)
      else if (data?.errors && typeof data.errors === 'object') {
        try {
          const msgs: string[] = [];
          for (const k of Object.keys(data.errors)) {
            const val = data.errors[k];
            if (Array.isArray(val)) {
              msgs.push(`${k}: ${val.join(' - ')}`);
            } else if (val) {
              msgs.push(`${k}: ${String(val)}`);
            }
          }
          if (msgs.length > 0) {
            errorMessage = `Error de validación del servidor:\n${msgs.join('\n')}`;
            // Mostrar cada error en un snackbar
            msgs.forEach((msg: string) => {
              enqueueSnackbar(msg, { variant: 'error', autoHideDuration: 6000 });
            });
            setError(errorMessage);
            mostrado = true;
          }
        } catch {}
      }
      // Manejar diferentes códigos de estado HTTP
      else if (!mostrado) {
        if (status === 400) {
          errorMessage = data?.title || data?.message || 'Solicitud inválida. Revise los datos ingresados.';
          if (data?.message) {
            errorMessage = data.message;
          }
          // Si el mensaje contiene múltiples líneas, mostrarlas por separado
          if (errorMessage.includes('\n')) {
            errorMessage.split('\n').forEach((msg: string) => {
              if (msg.trim()) {
                enqueueSnackbar(msg.trim(), { variant: 'error', autoHideDuration: 6000 });
              }
            });
          } else {
            enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 8000 });
          }
          setError(errorMessage);
          mostrado = true;
        } else if (status === 401 || status === 403) {
          errorMessage = 'No autorizado para crear pedidos. Verifique su sesión y permisos.';
          enqueueSnackbar(errorMessage, { variant: 'error' });
          setError(errorMessage);
          mostrado = true;
        } else if (status === 404) {
          // Mensaje más detallado para 404
          const urlIntentada = err?.config?.url || 'desconocida';
          errorMessage = `Endpoint no encontrado (404). URL intentada: ${urlIntentada}. Verifique que el endpoint esté disponible en el servidor.`;
          enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 10000 });
          setError(errorMessage);
          mostrado = true;
        } else if (status === 500) {
          errorMessage = data?.message || 'Error interno del servidor. Intente nuevamente o contacte con soporte.';
          enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 10000 });
          setError(errorMessage);
          mostrado = true;
        } else if (data?.message) {
          errorMessage = data.message;
          // Si el mensaje contiene múltiples líneas, mostrarlas por separado
          if (errorMessage.includes('\n')) {
            errorMessage.split('\n').forEach((msg: string) => {
              if (msg.trim()) {
                enqueueSnackbar(msg.trim(), { variant: 'error', autoHideDuration: 6000 });
              }
            });
          } else {
            enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 8000 });
          }
          setError(errorMessage);
          mostrado = true;
        }
      }

      // Si no se mostró un mensaje específico, mostrar el mensaje del error
      if (!mostrado) {
        const messageText = (data?.message || data || err?.message || '').toString();
        if (messageText && typeof messageText === 'string' && messageText.length > 0) {
          errorMessage = messageText.length > 200 ? messageText.substring(0, 200) + '...' : messageText;
          // Si el mensaje contiene múltiples líneas, mostrarlas por separado
          if (errorMessage.includes('\n')) {
            errorMessage.split('\n').forEach((msg: string) => {
              if (msg.trim()) {
                enqueueSnackbar(msg.trim(), { variant: 'error', autoHideDuration: 6000 });
              }
            });
          } else {
            enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 10000 });
          }
          setError(errorMessage);
        } else {
          errorMessage = 'Error al guardar el pedido de venta. Intente nuevamente.';
          enqueueSnackbar(errorMessage, { variant: 'error' });
          setError(errorMessage);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSuccessDialog = () => {
    setSuccessDialogOpen(false);
    // No cerrar el PDF automáticamente, el usuario puede cerrarlo cuando quiera
  };

  const handleClosePDF = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    setNroPedidoCreado(null);
    // Redirigir a la lista de pedidos cuando el usuario cierra el visor del PDF
    navigate('/ventas/pedidos');
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack 
            direction="row" 
            justifyContent="space-between" 
            alignItems="center" 
            sx={{ mb: 3 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
              {isReadOnly 
                ? `Ver Pedido de Venta #${pedidoId}`
                : isEditMode 
                  ? `Editar Pedido de Venta #${pedidoId}`
                  : 'Nuevo Pedido de Venta'}
            </Typography>
            {!isReadOnly && cotizacionId && infoCotizacion ? (
              <Chip
                label={
                  infoCotizacion.letra && (infoCotizacion.puntoVenta ?? 0) > 0 && (infoCotizacion.nroComprobante ?? 0) > 0
                    ? `Preparando pedido desde cotización ${infoCotizacion.letra}-${String(infoCotizacion.puntoVenta ?? 0).padStart(5, '0')}-${String(infoCotizacion.nroComprobante ?? 0).padStart(8, '0')} (#${infoCotizacion.nroFormulario})`
                    : `Preparando pedido desde cotización #${infoCotizacion.nroFormulario || cotizacionId}`
                }
                color="info"
                sx={{ fontWeight: 500 }}
              />
            ) : (isReadOnly || isEditMode) ? (
              <Button
                variant="outlined"
                onClick={() => navigate('/ventas/pedidos')}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
              >
                Volver
              </Button>
            ) : null}
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {loadingPedido ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography>Cargando pedido...</Typography>
            </Box>
          ) : (
            <PedidoForm
              pedidoVenta={pedidoVenta}
              onPedidoChange={setPedidoVenta}
              onClienteSearch={() => setClienteDialogOpen(true)}
              onProductoSearch={() => setProductoDialogOpen(true)}
              onSave={handleSave}
              loading={loading}
              readOnly={isReadOnly}
              saveButtonText={isEditMode ? 'Actualizar Pedido' : 'Guardar Pedido'}
            />
          )}
        </CardContent>
      </Card>

      <ClienteSearch
        open={clienteDialogOpen}
        onClose={() => setClienteDialogOpen(false)}
        onSelect={handleClienteSelect}
      />

      <ProductoSearch
        open={productoDialogOpen}
        onClose={() => setProductoDialogOpen(false)}
        onSearch={handleProductoSearch}
        onSelect={handleProductoSelect}
        productos={productos}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        loading={loading}
      />

      {/* Diálogo de éxito al crear pedido */}
      <Dialog
        open={successDialogOpen}
        onClose={handleCloseSuccessDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 6,
          }
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: '1.25rem',
            pb: 1.5,
            pt: 3,
            px: 3,
          }}
        >
          Pedido Creado
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2, px: 3 }}>
          <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            Pedido creado exitosamente! Número de Pedido: {nroPedidoCreado}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'flex-end' }}>
          <Button
            onClick={handleCloseSuccessDialog}
            variant="text"
            color="primary"
            sx={{
              minWidth: 80,
              textTransform: 'none',
              fontWeight: 500,
              color: 'primary.main',
            }}
            disabled={loadingPDF}
          >
            {loadingPDF ? 'Generando PDF...' : 'OK'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Visor de PDF del pedido */}
      {pdfUrl && nroPedidoCreado && (
        <PDFViewer
          pdfUrl={pdfUrl}
          onClose={handleClosePDF}
          title={`Pedido #${nroPedidoCreado}`}
          documentId={`${nroPedidoCreado}`}
          documentType="pedido"
        />
      )}
    </Box>
  );
};

export default PedidosVenta;